import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { LocalizationProvider, DateCalendar } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { format, isSameDay } from 'date-fns';
import { CreateBookingSlot } from '../../types/bookingSlot';
import { BookableService, BookingContactDetails } from '../../types/booking';

interface BookingModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** All bookable services. For shop profiles each is tied to a specific barber. */
  services: BookableService[];
  /** When provided, skips straight to the Date & Time step for this service. */
  preselectedService?: BookableService | null;
  /** Lazily loads (and caches) available slots, optionally filtered to one shop barber. */
  loadSlots: (barberId?: bigint) => Promise<CreateBookingSlot[]>;
  onConfirm: (
    slot: CreateBookingSlot,
    service: BookableService,
    contact: BookingContactDetails
  ) => Promise<void>;
}

const steps = ['Select Service', 'Date & Time', 'Your Details'];

/**
 * Shared multi-step booking flow modal used by both the public shop and
 * public independent barber profiles. Supports entering directly at the
 * Date & Time step when a specific service's "Select & Book" CTA is used,
 * or at the Service Selection step when the general "Book Now" CTA is used.
 */
const BookingModal: React.FC<BookingModalProps> = ({
  open,
  onClose,
  title,
  services,
  preselectedService,
  loadSlots,
  onConfirm,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedService, setSelectedService] =
    useState<BookableService | null>(null);
  const [slots, setSlots] = useState<CreateBookingSlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<CreateBookingSlot | null>(
    null
  );
  const [contact, setContact] = useState<BookingContactDetails>({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const resetFlow = (service: BookableService | null) => {
    setSelectedService(service);
    setActiveStep(service ? 1 : 0);
    setSelectedDate(null);
    setSelectedSlot(null);
    setContact({ customerName: '', customerEmail: '', customerPhone: '' });
    setSubmitError(null);
    setSubmitted(false);
  };

  // Re-initialise the flow's starting point whenever the modal is (re)opened.
  useEffect(() => {
    if (open) {
      resetFlow(preselectedService ?? null);
    }
  }, [open, preselectedService]);

  // Fetch slots for the relevant barber as soon as a service is selected.
  useEffect(() => {
    if (!open || !selectedService) return;

    let cancelled = false;
setSlots([]);
    setSlotsLoading(true);

    loadSlots(selectedService.barberId)
      .then((result) => {
        if (!cancelled) {
          setSlots(
            result.filter(
              (slot) =>
                slot.service_id === BigInt(selectedService.service.id || 0)
            )
          );
        }
      })
      .catch((err) => {
        console.error('Failed to load slots:', err);
        if (!cancelled) {
          setSlotsError(
            'Unable to load available slots. Please try again later.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) setSlotsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, selectedService, loadSlots]);

  const getSlotsForDate = (date: Date) =>
    slots.filter((slot) => isSameDay(slot.slot_date, date));

  const hasAvailableSlots = (date: Date) => getSlotsForDate(date).length > 0;

  const selectedDateSlots = useMemo(
    () => (selectedDate ? getSlotsForDate(selectedDate) : []),
    [selectedDate, slots]
  );

  const DayWithSlots = (props: PickersDayProps<Date>) => {
    const { day, outsideCurrentMonth, onDaySelect } = props;
    const hasSlots = !outsideCurrentMonth && hasAvailableSlots(day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const isPastDate = day < today;

    return (
      <PickersDay
        {...props}
        disabled={isPastDate || (!outsideCurrentMonth && !hasSlots)}
        onDaySelect={onDaySelect}
        sx={{
          ...(hasSlots && {
            bgcolor: '#dd8709',
            color: '#fff',
            fontWeight: 'bold',
            '&:hover': { bgcolor: '#b86e08' },
          }),
          ...(!hasSlots &&
            !outsideCurrentMonth &&
            !isPastDate && {
              color: 'rgba(0,0,0,0.3)',
            }),
        }}
      />
    );
  };

  const handleSelectService = (bookable: BookableService) => {
    setSelectedService(bookable);
    setSelectedDate(null);
    setSelectedSlot(null);
    setSlots([]);
    setActiveStep(1);
  };

  const handleSelectSlot = (slot: CreateBookingSlot) => {
    setSelectedSlot(slot);
  };

  const handleNextFromDateTime = () => {
    if (!selectedSlot) return;
    setActiveStep(2);
  };

  const handleBack = () => {
    if (activeStep === 1 && preselectedService) {
      // A "Select & Book" entry only has the Date & Time + Details steps.
      onClose();
      return;
    }
    setActiveStep((step) => Math.max(0, step - 1));
  };

  const isContactValid =
    contact.customerName.trim() &&
    contact.customerEmail.trim() &&
    contact.customerPhone.trim();

  const handleSubmit = async () => {
    if (!selectedSlot || !selectedService || !isContactValid) return;
    try {
      setSubmitting(true);
      setSubmitError(null);
      await onConfirm(selectedSlot, selectedService, contact);
      setSubmitted(true);
      setTimeout(() => {
        onClose();
      }, 1800);
    } catch (err) {
      console.error('Failed to create reservation:', err);
      setSubmitError('Failed to create reservation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1 }}>{title}</Box>
        <IconButton onClick={onClose} aria-label="Cancel booking">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stepper
          activeStep={activeStep}
          sx={{
            mb: 3,
            '& .MuiStepIcon-root.Mui-active': { color: '#dd8709' },
            '& .MuiStepIcon-root.Mui-completed': { color: '#dd8709' },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {services.length === 0 && (
              <Typography color="text.secondary">
                No services are currently available to book.
              </Typography>
            )}
            {services.map((bookable, i) => (
              <Card
                key={`${bookable.service.id}-${bookable.barberId ?? ''}-${i}`}
                variant="outlined"
              >
                <CardActionArea onClick={() => handleSelectService(bookable)}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {bookable.service.name}
                    </Typography>
                    {bookable.barberName && (
                      <Typography variant="body2" color="text.secondary">
                        with {bookable.barberName}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Chip
                        icon={<AttachMoneyIcon />}
                        label={`£${bookable.service.price ?? 0}`}
                        size="small"
                      />
                      <Chip
                        icon={<AccessTimeIcon />}
                        label={`${bookable.service.duration ?? 0} min`}
                        size="small"
                      />
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        )}

        {activeStep === 1 && (
          <Box>
            {slotsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {slotsError}
              </Alert>
            )}
            {slotsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateCalendar
                    value={selectedDate}
                    onChange={(value) => {
                      setSelectedDate(value);
                      setSelectedSlot(null);
                    }}
                    slots={{ day: DayWithSlots }}
                  />
                </LocalizationProvider>

                {selectedDate && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Available times - {format(selectedDate, 'MMMM d, yyyy')}
                    </Typography>
                    {selectedDateSlots.length === 0 ? (
                      <Typography color="text.secondary" variant="body2">
                        No available slots for this date.
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {selectedDateSlots.map((slot) => (
                          <Chip
                            key={slot.id}
                            label={format(slot.start_time, 'HH:mm')}
                            clickable
                            onClick={() => handleSelectSlot(slot)}
                            color={
                              selectedSlot?.id === slot.id
                                ? 'primary'
                                : 'default'
                            }
                            sx={
                              selectedSlot?.id === slot.id
                                ? { bgcolor: '#dd8709' }
                                : undefined
                            }
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>
        )}

        {activeStep === 2 && (
          <Box>
            {submitted ? (
              <Alert severity="success">
                Reservation created successfully! Confirmation emails sent.
              </Alert>
            ) : (
              <>
                {submitError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {submitError}
                  </Alert>
                )}
                {selectedSlot && selectedService && (
                  <Box
                    sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      Booking summary
                    </Typography>
                    <Typography variant="body1">
                      {selectedService.service.name} -{' '}
                      {format(selectedSlot.start_time, 'HH:mm')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {format(selectedSlot.slot_date, 'MMMM d, yyyy')}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Name"
                    value={contact.customerName}
                    onChange={(e) =>
                      setContact({ ...contact, customerName: e.target.value })
                    }
                    fullWidth
                    required
                    disabled={submitting}
                  />
                  <TextField
                    label="Phone Number"
                    value={contact.customerPhone}
                    onChange={(e) =>
                      setContact({
                        ...contact,
                        customerPhone: e.target.value,
                      })
                    }
                    fullWidth
                    required
                    disabled={submitting}
                    placeholder="07980123456"
                  />
                  <TextField
                    label="Email"
                    type="email"
                    value={contact.customerEmail}
                    onChange={(e) =>
                      setContact({
                        ...contact,
                        customerEmail: e.target.value,
                      })
                    }
                    fullWidth
                    required
                    disabled={submitting}
                  />
                </Box>
              </>
            )}
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 3,
            gap: 2,
          }}
        >
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {activeStep > 0 && !submitted && (
              <Button onClick={handleBack} disabled={submitting}>
                Back
              </Button>
            )}
            {activeStep === 1 && (
              <Button
                variant="contained"
                disabled={!selectedSlot}
                onClick={handleNextFromDateTime}
                sx={{ bgcolor: '#dd8709', '&:hover': { bgcolor: '#b86e08' } }}
              >
                Next
              </Button>
            )}
            {activeStep === 2 && !submitted && (
              <Button
                variant="contained"
                disabled={!isContactValid || submitting}
                onClick={handleSubmit}
                sx={{ bgcolor: '#dd8709', '&:hover': { bgcolor: '#b86e08' } }}
              >
                {submitting ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  'Create Booking'
                )}
              </Button>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
